import { isCurrency } from "@bitwarden/web-vault/app/models/view/symbol/currency-utility";

// ── What an asset is worth ───────────────────────────────────────────────────────────────────

/**
 * What the user holds of one symbol, in the base currency: the quantity held across every account
 * times the symbol's rate.
 *
 * Distinguishes "never had a transaction-derived quantity at all" (manually-valued assets like real
 * estate or personal property: there is no "buy" transaction for a house, so it counts as a single
 * unit and the asset's own price is its value) from "has transaction history but the net position
 * is zero or negative" (a stock bought and fully sold, or a short position): the latter must use
 * the real, possibly zero or negative, quantity, not silently fall back to the raw price. Currencies
 * never get the single-unit fallback even when untracked: an unheld forex-only currency has no
 * meaningful "quantity of 1" and would otherwise add a phantom amount to the Cash total.
 */
export function holdingValue(
  code: string,
  rate: number | undefined,
  qtyByCode: Map<string, number>,
): number {
  const unitRate = rate ?? 0;
  if (qtyByCode.has(code)) {
    return qtyByCode.get(code) * unitRate;
  }
  return isCurrency(code) ? 0 : unitRate;
}

// ── How old the price is ─────────────────────────────────────────────────────────────────────

/** A price this many whole days old (or older) is flagged as out of date. */
export const PRICE_STALE_AFTER_DAYS = 2;

/**
 * kind: "base" the user's base currency (it has no price of its own), "today" priced today, "old" priced
 * `days` days ago, "none" no price at all.
 */
export interface SymbolPriceStatus {
  level: "ok" | "warn" | "neutral";
  kind: "base" | "today" | "old" | "none";
  days: number;
}

/**
 * Whole calendar days between a price's date ("2026-10-15") and `now`; 0 for today or a date in the
 * future, null when the date is missing or unreadable. Compared as calendar dates, so a price from
 * 23:59 yesterday is 1 day old at 00:01 today.
 */
export function priceAgeInDays(
  asOfDate: string | undefined,
  now: Date = new Date(),
): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(asOfDate ?? "");
  if (!match) {
    return null;
  }
  const asOf = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.max(0, Math.round((today - asOf) / 86_400_000));
}

export function symbolPriceStatus(
  code: string,
  baseCurrency: string,
  asOfDate: string | undefined,
  now: Date = new Date(),
): SymbolPriceStatus {
  if (code === baseCurrency) {
    return { level: "neutral", kind: "base", days: 0 };
  }
  const days = priceAgeInDays(asOfDate, now);
  if (days === null) {
    return { level: "neutral", kind: "none", days: 0 };
  }
  return {
    level: days >= PRICE_STALE_AFTER_DAYS ? "warn" : "ok",
    kind: days === 0 ? "today" : "old",
    days,
  };
}

// ── The price line under a card ──────────────────────────────────────────────────────────────

/**
 * conversion: a holding in another currency than the base, as what it is worth in that currency and
 * the rate it is converted at ("639.00 USD @ 1:1.5650": a currency held, or a security quoted in a
 * foreign currency); unit: a price per unit in the base currency ("100.76 AUD a unit"); none:
 * nothing to say (the base currency, or no price yet), so the card shows its share of the assets
 * there instead.
 */
export type SymbolPriceLine =
  | { kind: "conversion"; amount: number; code: string; rate: number }
  | { kind: "unit"; rate: number; quote: string }
  | { kind: "none" };

/** One unit of a symbol in a foreign currency, and the rate that currency converts at. */
export interface UnitConversion {
  /** What one unit is worth in `code`: 1 for a currency, the quoted price for a security. */
  amount: number;
  code: string;
  /** What one unit of `code` is worth in the base currency. */
  rate: number;
}

type LatestPrice = { rate: number; symbol: string } | undefined;

function hasUsablePrice(code: string, baseCurrency: string, latest: LatestPrice): boolean {
  return code !== baseCurrency && !!latest && latest.rate > 0 && !!latest.symbol;
}

/**
 * What one unit of the symbol is in a foreign currency, and the rate that currency converts at:
 * "1 USD @ 1:1.5650" for a currency, "229.50 USD @ 1:1.5650" for a security quoted in USD. Null
 * for the base currency, a symbol with no usable price, one with no base rate to convert at, and a
 * security quoted in the base currency (or in something that is not a currency).
 *
 * `baseRate` is what one unit of the symbol is worth in the base currency (the rate the card's
 * value is built from). For a security quoted in a foreign currency the conversion's rate is the
 * base value of one unit of that currency: its base price over its quoted price.
 */
export function unitConversion(
  code: string,
  baseCurrency: string,
  baseRate: number | undefined,
  latest: LatestPrice,
): UnitConversion | null {
  if (!hasUsablePrice(code, baseCurrency, latest) || !(baseRate > 0)) {
    return null;
  }
  if (isCurrency(code)) {
    return { amount: 1, code, rate: baseRate };
  }
  if (latest.symbol !== baseCurrency && isCurrency(latest.symbol)) {
    return { amount: latest.rate, code: latest.symbol, rate: baseRate / latest.rate };
  }
  return null;
}

/**
 * `qtyByCode` is what is held of each code, so a conversion's `amount` times its `rate` is the
 * value on the card.
 */
export function symbolPriceLine(
  code: string,
  baseCurrency: string,
  baseRate: number | undefined,
  latest: LatestPrice,
  qtyByCode: Map<string, number>,
): SymbolPriceLine {
  if (!hasUsablePrice(code, baseCurrency, latest)) {
    return { kind: "none" };
  }
  const conversion = unitConversion(code, baseCurrency, baseRate, latest);
  if (conversion) {
    // An asset with no recorded holding (a house) counts as one unit, as in `holdingValue`; a
    // currency with none is zero of it.
    const held = qtyByCode.get(code) ?? (isCurrency(code) ? 0 : 1);
    return {
      kind: "conversion",
      amount: held * conversion.amount,
      code: conversion.code,
      rate: conversion.rate,
    };
  }
  return isCurrency(code)
    ? { kind: "none" }
    : { kind: "unit", rate: latest.rate, quote: latest.symbol };
}

/** `DecimalPipe` digits for a conversion rate: always 4 decimals (1.5650, 0.2015). */
export const CONVERSION_RATE_DIGITS = "1.4-4";

/**
 * `DecimalPipe` digits for a price: a currency rate needs 4 decimals (1.5650, 0.2015) to mean
 * anything; a security's price is read in cents (229.50).
 */
export function symbolPriceDigits(code: string): "1.4-4" | "1.2-2" {
  return isCurrency(code) ? "1.4-4" : "1.2-2";
}

// ── Names and tiles ──────────────────────────────────────────────────────────────────────────

/** Currencies written with a dollar sign that have to be told apart from the user's own. */
const DOLLAR_TILES: Record<string, string> = {
  AUD: "A$",
  CAD: "C$",
  HKD: "HK$",
  NZD: "NZ$",
  SGD: "S$",
  USD: "US$",
};

/**
 * The text on an asset's tile: "US$" for a dollar currency, the three letters for any other
 * currency, and the first four characters of the code for everything else ("AAPL", "VAS").
 */
export function symbolTileText(code: string): string {
  const upper = (code ?? "").toUpperCase();
  if (isCurrency(upper)) {
    return DOLLAR_TILES[upper] ?? upper;
  }
  return upper.slice(0, 4);
}

/** "Australian Dollar" for "AUD"; the code itself when the runtime does not know the currency. */
export function currencyFullName(code: string): string {
  try {
    return new Intl.DisplayNames(["en"], { type: "currency" }).of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

/**
 * What a card calls an asset: the name the user gave it, else the currency's full name, else the
 * code. A forex-generated currency symbol has no name of its own.
 */
export function symbolDisplayName(code: string, name: string | undefined): string {
  const given = (name ?? "").trim();
  if (given) {
    return given;
  }
  return isCurrency(code) ? currencyFullName(code) : code;
}
